import React, { useEffect, useState } from 'react';
import { CircularProgress, styled, keyframes } from '@mui/material';
import {
  Refresh,
  ArrowBack,
  OpenInNew,
  ErrorOutline,
} from '@mui/icons-material';
import { PrimaryButton, NeutralButton } from './component/common';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const checkmarkDraw = keyframes`
  0% {
    stroke-dashoffset: 50;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

// Styled components
const PageContainer = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)',
  animation: `${fadeIn} 0.3s ease-out`,
});

const ContentCard = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px 32px',
  maxWidth: '480px',
  width: '90%',
  animation: `${slideUp} 0.5s ease-out`,
  animationDelay: '0.1s',
  animationFillMode: 'backwards',
});

const IconCircle = styled('div')(({ variant }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: variant === 'error' ? '#FFEBEE' : '#E8F5E9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
  '& svg': {
    width: '40px',
    height: '40px',
    stroke: variant === 'error' ? '#D32F2F' : '#4CAF50',
    strokeWidth: 3,
    fill: 'none',
    '& polyline': {
      strokeDasharray: 50,
      strokeDashoffset: 0,
      animation: `${checkmarkDraw} 0.5s ease-out`,
      animationDelay: '0.3s',
      animationFillMode: 'backwards',
    },
  },
}));

const Title = styled('h1')({
  fontSize: '28px',
  fontWeight: 600,
  color: '#1a1a1a',
  margin: '0 0 12px 0',
  textAlign: 'center',
});

const Message = styled('p')({
  fontSize: '15px',
  color: '#666',
  margin: '0 0 32px 0',
  textAlign: 'center',
  lineHeight: 1.6,
  maxWidth: '360px',
});

const RedirectMessage = styled('p')({
  fontSize: '15px',
  color: '#666',
  margin: '0 0 8px 0',
  textAlign: 'center',
  animation: `${pulse} 2s ease-in-out infinite`,
});

const RedirectUrl = styled('a')({
  fontSize: '14px',
  color: '#1976d2',
  textDecoration: 'none',
  wordBreak: 'break-all',
  textAlign: 'center',
  marginBottom: '16px',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const Countdown = styled('span')({
  fontSize: '13px',
  color: '#999',
  marginTop: '8px',
});

const ButtonGroup = styled('div')({
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginTop: '8px',
});

const ErrorCard = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px',
  backgroundColor: '#FFF8F8',
  borderRadius: '12px',
  border: '1px solid #FFCDD2',
  marginBottom: '24px',
  width: '100%',
  maxWidth: '400px',
});

const ErrorIconWrapper = styled('div')({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: '#FFEBEE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '16px',
  '& svg': {
    fontSize: '28px',
    color: '#D32F2F',
  },
});

const ErrorTitle = styled('h2')({
  fontSize: '18px',
  fontWeight: 600,
  color: '#C62828',
  margin: '0 0 8px 0',
  textAlign: 'center',
});

const ErrorText = styled('p')({
  fontSize: '14px',
  color: '#666',
  margin: 0,
  textAlign: 'center',
  lineHeight: 1.5,
});

const SessionId = styled('div')({
  fontSize: '12px',
  color: '#999',
  backgroundColor: '#f5f5f5',
  padding: '8px 16px',
  borderRadius: '6px',
  fontFamily: 'monospace',
  marginTop: '16px',
  wordBreak: 'break-all',
});

// Checkmark SVG component
const CheckmarkIcon = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const REDIRECT_DELAY_MS = 0;

// Ensure URL has a protocol for proper redirect
const ensureUrlProtocol = (url) => {
  if (!url || url.trim() === '') return url;
  const trimmedUrl = url.trim();
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  return `https://${trimmedUrl}`;
};

const SubmissionReceipt = (props) => {
  const [countdown, setCountdown] = useState(
    Math.ceil(REDIRECT_DELAY_MS / 1000)
  );

  // Handle external URL redirect with delay
  useEffect(() => {
    if (props.submissionState === 'redirecting' && props.redirectUrl) {
      const normalizedUrl = ensureUrlProtocol(props.redirectUrl);

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      const redirectTimeout = setTimeout(() => {
        window.location.href = normalizedUrl;
      }, REDIRECT_DELAY_MS);

      return () => {
        clearTimeout(redirectTimeout);
        clearInterval(countdownInterval);
      };
    }
  }, [props.submissionState, props.redirectUrl]);

  const handleBack = () => {
    if (props.testId !== undefined) {
      window.location.pathname = `form/form_test/${props.formId}/${props.testId}/${props.sessionId}`;
    } else {
      window.location.pathname = `form/${props.formId}/${props.sessionId}`;
    }
  };

  const handleRestart = () => {
    if (!props || !props.formId) return;
    if (props.formType !== 'form') return;
    window.open(`/form/${props.formId}`, '_self');
  };

  // Redirecting to external URL state
  if (props.submissionState === 'redirecting') {
    const normalizedUrl = ensureUrlProtocol(props.redirectUrl);
    let displayUrl = normalizedUrl;
    if (normalizedUrl && normalizedUrl.length > 50) {
      try {
        displayUrl = new URL(normalizedUrl).hostname;
      } catch {
        displayUrl = normalizedUrl.substring(0, 50) + '...';
      }
    }

    return (
      <PageContainer>
        <ContentCard>
          <IconCircle>
            <CheckmarkIcon />
          </IconCircle>
          <Title>Response submitted</Title>
          <RedirectMessage>Redirecting you to</RedirectMessage>
          <RedirectUrl
            href={normalizedUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {displayUrl}
          </RedirectUrl>
          <CircularProgress
            size={24}
            thickness={4}
            sx={{ color: '#1976d2', marginTop: '16px' }}
          />
        </ContentCard>
      </PageContainer>
    );
  }

  // Error state
  if (props.submissionState === 'failure') {
    return (
      <PageContainer>
        <ContentCard>
          <ErrorCard>
            <ErrorIconWrapper>
              <ErrorOutline />
            </ErrorIconWrapper>
            <ErrorTitle>Something went wrong</ErrorTitle>
            <ErrorText>
              We encountered an error while submitting your response. Please try
              again or contact support.
            </ErrorText>
            {props.sessionId && (
              <SessionId>Session: {props.sessionId}</SessionId>
            )}
          </ErrorCard>
          <ButtonGroup>
            <NeutralButton startIcon={<ArrowBack />} onClick={handleBack}>
              Go Back
            </NeutralButton>
          </ButtonGroup>
        </ContentCard>
      </PageContainer>
    );
  }

  // Success state
  return (
    <PageContainer>
      <ContentCard>
        <IconCircle>
          <CheckmarkIcon />
        </IconCircle>
        <Title>Response submitted</Title>
        <Message>
          {props.submMsg && props.submMsg.trim() !== ''
            ? props.submMsg
            : 'Your response has been successfully submitted. Thank you!'}
        </Message>
        {props.formType === 'form' && props.canAddSubmission && (
          <ButtonGroup>
            <PrimaryButton startIcon={<Refresh />} onClick={handleRestart}>
              Submit Another Response
            </PrimaryButton>
          </ButtonGroup>
        )}
      </ContentCard>
    </PageContainer>
  );
};

export default SubmissionReceipt;
